import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, Output, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { AI_ALLOWED_EXT, AiDocRepoFileModel, AiDocRepoFolderModel, LucaEngListModel, UserDetailModel } from 'countable@model';
import { AiService } from 'countable@services';
import { saveAs } from 'file-saver';
import { AutoDocDataModel, DocsSortModel, FolderType, SORT_DOCS_LIST } from 'src/app/model/docrepo/auto-doc-data.model';
import { EngagementDataModel } from 'src/app/model/docrepo/engagement-data.model';
import { EngagementTypeDataModel } from 'src/app/model/docrepo/engagement-type-data.model';
import { FileDataModel } from 'src/app/model/docrepo/file-data.model';
import { YearDataModel } from 'src/app/model/docrepo/year-data.model';
import { ApiService } from 'src/app/services/api.service';
import { ExtensionService } from 'src/app/services/extensions.service';
import { DocumentRepoService } from '../../../services/document-repo.service';

@Component({
  selector: 'app-ai-document-repo',
  templateUrl: './ai-document-repo.component.html',
  styleUrl: './ai-document-repo.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AiDocumentRepoComponent {

  @Input() set docRepoInfo(data: any) {
    if (data && data?.userDetails) {
      this.loggedInUserData = structuredClone(data?.userDetails);
      this.userLoginId = this.loggedInUserData.useracctid;
      this.bucketName = this.loggedInUserData.s3BucketName;
      this.firmId = this.loggedInUserData.firmUserAcctId;
    }
    if (data && data?.selectedEngagement) {
      this.selectedEng.set(structuredClone(data?.selectedEngagement));
      this.clientFirmId = this.selectedEng()?.clientId;
      this.engagementId = this.selectedEng()?.engId;
      this.getAutoDocRepoByClient();
      this.getAllFolders();
    }
  }

  @Output() closeDocRepo: EventEmitter<any> = new EventEmitter<any>(true);
  @Output() selectedFiles: EventEmitter<FileDataModel[]> = new EventEmitter<FileDataModel[]>(true);

  docSearch: FormControl<string> = new FormControl('');
  bucketName: string;
  firmId: number;
  userLoginId: any;
  loggedInUserData: UserDetailModel;
  folderList: any[] = [];
  sort: DocsSortModel[] = SORT_DOCS_LIST;
  isSearchInput: boolean = false;
  selectedEngagement: any;
  tokenDetails: any;
  clientFirmId: any;
  engagementId: any;

  // Automated folders
  tabIndex: number = 0;
  autoDocFolderList: AutoDocDataModel[];
  isExpandAll = false;
  searchResults: FileDataModel[] = [];
  public FolderType: typeof FolderType = FolderType;
  public selectedEng: WritableSignal<LucaEngListModel> = signal<LucaEngListModel>(null);
  public selectedDocumentsCount: WritableSignal<number> = signal<number>(0);
  public clientFoldersList: AiDocRepoFolderModel[] = [];
  private allowedExtensions = [...AI_ALLOWED_EXT];
  private destroyRef = inject(DestroyRef)

  constructor(public apiService: ApiService, public aiService: AiService, private cdr: ChangeDetectorRef,
              private extensionService: ExtensionService, private documentRepoService: DocumentRepoService
  ) {
    this.docSearch.valueChanges.subscribe((text: string) => {
      this.searchResults = [];
      if (text && text?.trim()?.length > 0) {
        this.getSearchResults(text?.toLowerCase());
      }
    });
  }

  getAllFolders() {
    let clientUserAccountId = this.loggedInUserData?.useracctid;
    let orderType = 1;
    let searchTxt = '';
    let clientFirmId = this.clientFirmId;

    this.documentRepoService.getInsideEngDocRepo(clientUserAccountId, orderType, searchTxt, clientFirmId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          if (res.status === 200) {
            this.transformFolderList(JSON.parse(res?.data) ?? []);
          }
        }
      });
  }

  download(data: any) {
    this.apiService.fileDownload(JSON.stringify(
      {'s3FileDownlaodPath': `https://s3.amazonaws.com/${this.bucketName}/${data.filepath}`}
    )).subscribe({
      next: response => {
        if (response) {
          let downloadFileName = data.filenamechange ? data.filenamechange : data.filename;
          saveAs(response, downloadFileName);
        }
      }, error: error => {
      }
    });
  }

  private getAutoDocRepoByClient() {
    this.aiService.fetchAutoDocRepoByClient(this.selectedEng()?.clientId).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: res => {
          if (res?.status === 200 && res?.data?.years?.length > 0) {
            this.autoDocFolderList = [{...res?.data}];
            this.autoDocFolderList?.forEach((item: AutoDocDataModel) => {
              item?.years?.forEach((year: YearDataModel) => {
                year?.engagementTypes?.forEach((type: EngagementTypeDataModel) => {
                  type?.engagements?.forEach((eng: EngagementDataModel) => {
                    if (eng?.workingPapers) {
                      if (eng?.workingPapers?.systemDocuments) {
                        eng.workingPapers.isSystemDocumentsSelected = false;
                        eng.workingPapers.systemDocuments.forEach(doc => {
                          doc.isSelected = false;
                          doc.hasValidFile = this.allowedExtensions.includes(doc?.extension);

                        });
                      }
                      if (eng?.workingPapers?.manualDocuments) {
                        eng.workingPapers.isManualDocumentsSelected = false;
                        eng.workingPapers.manualDocuments.forEach(doc => {
                          doc.isSelected = false;
                          doc.hasValidFile = this.allowedExtensions.includes(doc?.extension);
                        });
                      }
                      if (eng?.finalDocuments) {
                        eng.isFinalDocumentsSelected = false;
                        eng.finalDocuments.forEach(doc => {
                          doc.isSelected = false;
                          doc.hasValidFile = this.allowedExtensions.includes(doc?.extension);
                        });
                      }
                    }
                  });
                });
              });
            });
            this.cdr.markForCheck();
          }
      }
    })
  }

  // Finds search term in folders
  getSearchResults(searchText: string) {
    this.searchResults = [];
    this.searchDocRepoFiles(searchText.toLowerCase());
    this.searchAutoDocFiles(JSON.parse(JSON.stringify(this.autoDocFolderList)), searchText.toLowerCase());
  }

  // Searches files in doc repo Added Folders
  searchDocRepoFiles(searchText: string) {
    this.clientFoldersList.forEach(folder => {
      // Files in folder
      if (folder.childFiles.length > 0) {
        folder.childFiles.forEach(file => {
          if (file?.fileName?.toLowerCase()?.includes(searchText)) {
            this.searchResults.push({
              isClientDocument: true,
              engLeftSideDocId: 0,
              filename: file.fileName,
              extension: file.ext,
              mssdkfileid: '',
              filepath: file.filePath,
              userDocumentName: file.nameWithExt,
              fileCode: '',
              logicalPath: folder.parentFolderName,
              isChecked: file.isChecked,
              isUploaded: false,
              downloadName: file.nameSummary,
              sortOrder: 0,
              engteamclientdocid: file.fileId,
              lastUpdated: '',
              isSelected: file.isChecked
            });
          }
        });
      }
    });
  }

  // Searches files in automated folders
  searchAutoDocFiles(docsToSearch: AutoDocDataModel[], searchText: string) {
    docsToSearch?.forEach(client => {
      client?.years?.forEach(year => {
        year?.engagementTypes?.forEach(engType => {
          engType?.engagements?.forEach(eng => {
            this.searchResults.push(...eng?.workingPapers?.systemDocuments?.filter(file => {
              if (file?.filename?.toLowerCase()?.includes(searchText)) {
                file.logicalPath = year.year + '>' + engType.type + '>' + eng.engagementName + '> Working Papers > System Documents';
                file.engagementId = eng.engagementId;
                file.folderType = FolderType.SystemDocuments;
                file.isAutoDocument= true;
                return file;
              }
            }));
            this.searchResults.push(...eng?.workingPapers?.manualDocuments?.filter(file => {
              if (file?.filename?.toLowerCase().includes(searchText)) {
                file.logicalPath = year.year + '> ' + engType.type + '> ' + eng.engagementName + '> Working Papers> Manual Documents';
                file.isAutoDocument= true;
                file.engagementId = eng.engagementId;
                file.folderType = FolderType.ManualDocuments;
                return file;
              }
            }));
            this.searchResults.push(...eng.finalDocuments.filter(file => {
              if (file?.filename?.toLowerCase()?.includes(searchText)) {
                file.logicalPath = year.year + '> ' + engType.type + '> ' + eng.engagementName + '> Final Documents';
                file.isAutoDocument= true;
                file.engagementId = eng.engagementId;
                file.folderType = FolderType.FinalDocuments;
                return file;
              }
            }));
          });
        });
      });
    });
  }

  checkFolderExpandOrCollapse() {
    let isAllOpen = false;
    let mainFolders = false;
    setTimeout(() => {

      this.clientFoldersList?.forEach(f => {
        if (f.isExpand) {
          mainFolders = true;
        }
        f?.childFiles?.forEach(c => {
          if (c?.isExpand) {
            isAllOpen = true;
          }
        });
      })

      this.autoDocFolderList?.forEach(p => {
        p?.years?.forEach(s => {
              if (s['isExpand']) {
                mainFolders = true;
              }
          s?.engagementTypes?.forEach(a => {
                  if (a['isExpand']) {
                    isAllOpen = true;
                  }
            a?.engagements?.forEach(b => {
                      if (b['isExpand']) {
                        isAllOpen = true;
                      }
            })
          })
            })
      })

      setTimeout(() => {
        this.isExpandAll = mainFolders;
      }, 100);
      this.cdr.markForCheck();
    }, 500);
  }

  toggleExpandOrCollapse(isExpandAll: boolean) {

    const foldersList: AiDocRepoFolderModel[] = JSON.parse(JSON.stringify(this.clientFoldersList));
    foldersList?.forEach(f => {
      f.isExpand = isExpandAll;
      f?.childFiles?.forEach(c => {
        c.isExpand = isExpandAll;
      });
    });

    const autoDocsFolder: AutoDocDataModel[] = JSON.parse(JSON.stringify(this.autoDocFolderList));
    autoDocsFolder?.forEach(p => {
        p?.years?.forEach(s => {
            s['isExpand'] = isExpandAll;
          s?.engagementTypes?.forEach(a => {
            a['isExpand'] = isExpandAll;
            a?.engagements?.forEach(b => {
              b['isExpand'] = isExpandAll;
            });
          });
        })
      });

    this.autoDocFolderList = JSON.parse(JSON.stringify(autoDocsFolder));
    this.clientFoldersList = JSON.parse(JSON.stringify(foldersList));
    this.cdr.markForCheck();
    this.isExpandAll = isExpandAll;
  }

  private transformFolderList(foldersList: any[]) {
    if (!Array.isArray(foldersList) || !foldersList) {
      return null;
    }
    const transformedList: AiDocRepoFolderModel[] = [];

    foldersList?.forEach((folder: any) => {
      transformedList.push({
        parentFolderId: folder?.partnerfolderid,
        isChecked: false,
        isExpand: false,
        fileCount: folder?.count ? folder?.count : 0,
        parentFolderName: folder?.partnerfoldername ? folder?.partnerfoldername : '',
        childFiles: this.transformFileList(folder?.parentfiles)
      });
    });
    this.clientFoldersList = (JSON.parse(JSON.stringify(transformedList)));
    this.cdr.markForCheck();
  }

  private transformFileList(files: any[]) {
    if (!Array.isArray(files) || !files) {
      return [];
    }
    const allowedExtensions = ['pdf', 'txt', 'docx', 'csv', 'xlsx', 'xls', 'gif', 'jpg', 'jpeg', 'pptx'];
    const transformedList: AiDocRepoFileModel[] = [];

    files?.forEach((file: any) => {
      transformedList.push({
        fileId: file?.engteamclientdocid,
        ext: file?.extension,
        extImgPath: file?.extensionspath,
        fileName: file?.filename,
        filePath: file?.filepath,
        parentFolderId: file?.partnerfolderid,
        isChecked: false,
        isExpand: false,
        nameWithExt: file?.withoutextension,
        nameSummary: this.convertString(file?.filename),
        hasValidFile: allowedExtensions.includes(file?.extension)
      });
    });

    return transformedList;
  }

  private convertString(input: string | null | undefined): string {
    if (input && input.length > 20) {
      return input.substring(0, 20) + '...';
    }
    return input || '';
  }


  handleImageError(file: any, event: any) {
    file.originalfilename = file?.filename;
    event.target.src = this.extensionService.getExtensionPath(file);
  }

  handleErrorImage(fileName: string, ext: string, event: any) {
    event.target.src = this.extensionService.getExtensionPath({'originalfilename': fileName, 'extension': ext});
  }

  filterCheckedAutoDocs(data: AutoDocDataModel[]): FileDataModel[] {
    const selectedFiles: FileDataModel[] = [];
    data?.forEach((item: AutoDocDataModel) => {
      item?.years?.forEach((year: YearDataModel) => {
        year?.engagementTypes?.forEach((type: EngagementTypeDataModel) => {
          type?.engagements?.forEach((engagement: EngagementDataModel) => {
            if (engagement?.workingPapers) {
              if (engagement?.workingPapers?.systemDocuments) {
                engagement?.workingPapers?.systemDocuments?.forEach((doc: FileDataModel) => {
                  if (doc?.isSelected) {
                    selectedFiles.push(doc);
                  }
                });
              }
              if (engagement?.workingPapers?.manualDocuments) {
                engagement?.workingPapers?.manualDocuments?.forEach((doc: FileDataModel) => {
                  if (doc?.isSelected) {
                    selectedFiles.push(doc);
                  }
                });
              }
            }
            if (engagement?.finalDocuments) {
              engagement?.finalDocuments?.forEach((doc: FileDataModel) => {
                if (doc?.isSelected) {
                  selectedFiles.push(doc);
                }
              });
            }
          });
        });
      });
    });
    return selectedFiles;
  }

  filterCheckedClientDocs(foldersList: AiDocRepoFolderModel[]) {
    const selectedFiles: AiDocRepoFileModel[] = [];
    foldersList?.forEach(folder => {
      folder?.childFiles?.forEach(file => {
        if (file?.isChecked) {
          selectedFiles.push(file);
        }
      });
    });
    return selectedFiles;
  }

  filterCheckedFiles() {
    const clientSelectedFiles = this.filterCheckedClientDocs(JSON.parse(JSON.stringify(this.clientFoldersList)));
    const transformClientFiles = this.mapToFileDataModel(clientSelectedFiles);
    const autoDocSelectedFiles = this.filterCheckedAutoDocs(JSON.parse(JSON.stringify(this.autoDocFolderList)));
    return structuredClone([...transformClientFiles, ...autoDocSelectedFiles]);
  }

  uploadSelectedFiles(type: 'UPLOAD' | 'CLOSE') {
    if (type === 'CLOSE') {
      this.closeDocRepo.emit({'isUpload': false});
      return;
    }
    const allFiles = this.filterCheckedFiles();
    if ((allFiles && allFiles?.length === 0) || !allFiles) {
      this.closeDocRepo.emit({'isUpload': false});
      return;
    }
    this.closeDocRepo.emit({'isUpload': true, 'selectedFiles': structuredClone(allFiles)});
  }

  selectCheckBox(event: MatCheckboxChange, doc: any) {
    const isChecked = event.checked;
    if(doc.isAutoDocument) {
      this.autoDocFolderList?.forEach(client => {
        client?.years?.forEach(year => {
          year?.engagementTypes?.forEach(type => {
            type?.engagements?.forEach(eng => {
              if (eng.engagementId === doc.engagementId) {
                if (doc.folderType === FolderType.SystemDocuments) {
                  if (eng?.workingPapers?.systemDocuments) {
                    if(!isChecked) {
                      eng.workingPapers.isSystemDocumentsSelected = false;
                    }
                    eng.workingPapers.systemDocuments.forEach(e => {
                      if(doc.filepath === e.filepath) {
                        e.isSelected = isChecked;
                      }
                    });
                  }
                } else if (doc.folderType === FolderType.ManualDocuments) {
                  if (eng?.workingPapers?.manualDocuments) {
                    if(!isChecked) {
                      eng.workingPapers.isManualDocumentsSelected = false;
                    }
                    eng.workingPapers.manualDocuments.forEach(f => {
                      if(doc.filepath === f.filepath) {
                        f.isSelected = isChecked;
                      }
                    });
                  }
                } else if (doc.folderType === FolderType.FinalDocuments) {
                  if (eng?.finalDocuments) {
                    if(!isChecked) {
                      eng.isFinalDocumentsSelected = false;
                    }
                    eng.finalDocuments.forEach(g => {
                      if(doc.filepath === g.filepath) {
                        g.isSelected = isChecked;
                      }
                    });
                  }
                }
              }
            })
          })
        })
      });

    } else if(doc.isClientDocument) {
      this.clientFoldersList?.forEach(f => {
        f?.childFiles?.forEach(c => {
          if (c?.filePath === doc?.filepath) {
            c.isChecked = isChecked;
            if(!isChecked) {
              f.isChecked = false;
            }
          }
        });
      });
    }
    this.cdr.markForCheck();
    this.setCheckedCount();
  }

  public checkUncheckFolders(event: MatCheckboxChange, type: 'FOLDER' | 'FILE', folderId: number = 0, fileId: number = 0) {
    if (type === 'FOLDER') {
      this.clientFoldersList?.forEach(f => {
        if ( f?.parentFolderId === folderId ) {
            f?.childFiles?.forEach(c => {
              if (c.hasValidFile) {
                c.isChecked = event?.checked;
              }
          });
        }
      });
    }
    if (type === 'FILE') {
      this.clientFoldersList?.forEach(f => {
        if (f?.parentFolderId === folderId) {
          f.isChecked = f?.childFiles?.every(f => f?.isChecked === true);
        }
      });
    }
    this.setCheckedCount();
  }

  public checkUncheckAutoFolders(event: MatCheckboxChange, engagement: EngagementDataModel, folderType: FolderType, originType: 'FOLDER' | 'FILE') {
    this.autoDocFolderList?.forEach((item: AutoDocDataModel) => {
      item?.years?.forEach((year: YearDataModel) => {
        year?.engagementTypes?.forEach((type: EngagementTypeDataModel) => {
          type?.engagements?.forEach((eng: EngagementDataModel) => {
            if (eng.engagementId === engagement.engagementId) {
              if (folderType === FolderType.SystemDocuments) {
                if (eng?.workingPapers?.systemDocuments) {
                  (originType === 'FOLDER') && eng.workingPapers.systemDocuments.forEach(doc => {
                    if (doc.hasValidFile) {
                    doc.isSelected = event?.checked;
                    }
                  });
                  eng.workingPapers.isSystemDocumentsSelected = eng?.workingPapers?.systemDocuments?.every(d => d?.isSelected === true);
                }
              } else if (folderType === FolderType.ManualDocuments) {
                if (eng?.workingPapers?.manualDocuments) {
                  (originType === 'FOLDER') && eng.workingPapers.manualDocuments.forEach(doc => {
                    if (doc.hasValidFile) {
                    doc.isSelected = event?.checked;
                    }
                  });
                  eng.workingPapers.isManualDocumentsSelected = eng?.workingPapers?.manualDocuments?.every(d => d?.isSelected === true);
                }
              } else if (folderType === FolderType.FinalDocuments) {
                if (eng.finalDocuments) {
                  (originType === 'FOLDER') && eng.finalDocuments.forEach(doc => {
                    if (doc.hasValidFile) {
                    doc.isSelected = event?.checked;
                    }
                  });
                  eng.isFinalDocumentsSelected = eng?.finalDocuments?.every(d => d?.isSelected === true);
                }
              }
            }
          });
        });
      });
    });
    this.cdr.markForCheck();
    this.setCheckedCount();
  }

  private setCheckedCount() {
    const autoFolderFiles = this.filterCheckedAutoDocs(JSON.parse(JSON.stringify(this.autoDocFolderList)));
    const autoLength = autoFolderFiles?.length ? autoFolderFiles?.length : 0;
    const clientFolderFiles = this.filterCheckedClientDocs(JSON.parse(JSON.stringify(this.clientFoldersList)));
    const clientLength = clientFolderFiles?.length ? clientFolderFiles?.length : 0;
    const totalSelected = autoLength + clientLength;
    this.selectedDocumentsCount.set(totalSelected);
}

  private mapToFileDataModel(selectedFiles: AiDocRepoFileModel[], hasLogicalPath: boolean = false): FileDataModel[] {
    return selectedFiles.map(file => ({
      engLeftSideDocId: 0,
      filename: file?.fileName,
      extension: file?.ext,
      mssdkfileid: '',
      filepath: file?.filePath,
      userDocumentName: file?.nameWithExt,
      fileCode: '',
      logicalPath: file?.searchFilePath,
      isChecked: file.isChecked,
      isUploaded: false,
      downloadName: file.nameSummary,
      sortOrder: 0,
      engteamclientdocid: file.fileId,
      lastUpdated: '',
      isSelected: file.isChecked
    }));
  }

  clearSearch(){
    this.docSearch.setValue('');
    this.searchResults = [];
  }

}
